import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Switch,
  useToast,
  Container,
  Card,
  CardBody,
  Text,
  Badge,
  Flex,
  TableContainer,
  Icon,
} from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import { getData, postData } from "../../redux/actions/common.action";

// 1. Define the Interface for a Field
interface FieldConfig {
  fieldName: string;
  label: string;
  isRequired: boolean;
  isVisible: boolean;
  dataType?: string;
}

// 2. Updated Default Fields List
const DEFAULT_FIELDS: FieldConfig[] = [
  { fieldName: "Name", label: "Full Name", isRequired: true, isVisible: true },
  {
    fieldName: "EnquiryDate",
    label: "Enquiry Date",
    isRequired: true,
    isVisible: true,
  },
  {
    fieldName: "courseId",
    label: "Product / Course",
    isRequired: false,
    isVisible: true,
  },
  {
    fieldName: "CoursePrice",
    label: "Product Price",
    isRequired: false,
    isVisible: true,
  },
  {
    fieldName: "Email",
    label: "Email Address",
    isRequired: false,
    isVisible: true,
  },
  {
    fieldName: "Phone1",
    label: "Phone 1 (Primary)",
    isRequired: true,
    isVisible: true,
  },
  {
    fieldName: "Phone2",
    label: "Phone 2 (Secondary)",
    isRequired: false,
    isVisible: true,
  },
  {
    fieldName: "FollowupDate",
    label: "Follow Up Date",
    isRequired: false,
    isVisible: true,
  },
  { fieldName: "statusId", label: "Status", isRequired: true, isVisible: true }, // Using ID field names usually mapped in backend
  {
    fieldName: "branchId",
    label: "Branch",
    isRequired: true,
    isVisible: true,
  },
  { fieldName: "cityId", label: "City", isRequired: true, isVisible: true },
  {
    fieldName: "assignId",
    label: "Assign To",
    isRequired: true,
    isVisible: true,
  },
  {
    fieldName: "sourceId",
    label: "Source",
    isRequired: false,
    isVisible: true,
  },
  {
    fieldName: "location",
    label: "Location",
    isRequired: false,
    isVisible: true,
  },
  {
    fieldName: "image",
    label: "Attachment (Screenshot)",
    isRequired: false,
    isVisible: true,
  },
  {
    fieldName: "Remarks",
    label: "Remarks",
    isRequired: false,
    isVisible: true,
  },
];

const LeadConfiguration = () => {
  const dispatch = useDispatch<any>();
  const toast = useToast();
  const navigate = (path: string) => {};

  const { leadFieldConfig, loading } = useSelector(
    (state: any) => state.common,
  );

  const [localFields, setLocalFields] = useState<FieldConfig[]>(DEFAULT_FIELDS);

  // Fetch Config on Mount
  useEffect(() => {
    dispatch(getData("lead-field-config"));
  }, [dispatch]);

  // Sync Redux State to Local State
  useEffect(() => {
    if (
      leadFieldConfig &&
      leadFieldConfig.fields &&
      leadFieldConfig.fields.length > 0
    ) {
      const mergedFields = DEFAULT_FIELDS.map((defField) => {
        const dbField = leadFieldConfig.fields.find(
          (f: FieldConfig) => f.fieldName === defField.fieldName,
        );
        return dbField ? dbField : defField;
      });
      setLocalFields(mergedFields);
    }
  }, [leadFieldConfig]);

  // Handle Logic
  const handleToggle = (index: number, key: "isRequired" | "isVisible") => {
    const updatedFields = [...localFields];
    const field = updatedFields[index];

    if (key === "isRequired") {
      // Toggle mandatory status
      field.isRequired = !field.isRequired;

      // LOGIC: If a field becomes Mandatory, it MUST be Visible.
      if (field.isRequired) {
        field.isVisible = true;
      }
    } else if (key === "isVisible") {
      // Toggle visibility
      field.isVisible = !field.isVisible;

      // LOGIC: If a field is currently Mandatory, you shouldn't be able to hide it.
      // (The switch is disabled in UI, but this is a safety check).
      if (field.isRequired && !field.isVisible) {
        field.isVisible = true; // Revert immediately
        toast({
          title: "Action Denied",
          description: "Mandatory fields cannot be hidden.",
          status: "warning",
          duration: 2000,
          isClosable: true,
        });
      }
    }
    setLocalFields(updatedFields);
  };

  const handleSave = () => {
    const payload = {
      fields: localFields,
    };
    dispatch(postData(payload, "lead-field-config", navigate, toast));
  };

  return (
    <Box p={{ base: 2, md: 4 }} bg="gray.50" minH="100vh" w="100%">
      {/* Container set to full width */}
      <Container maxW="100%" px={{ base: 1, md: 4 }}>
        {/* Top Control Bar */}
        <Card mb={4} variant="outline" boxShadow="sm" bg="white" w="100%">
          <CardBody py={3}>
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              gap={4}
            >
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Icon as={InfoOutlineIcon} color="orange.400" />
                  <Text fontWeight="bold" fontSize="md" color="gray.700">
                    Configuration Rules
                  </Text>
                </Flex>
                <Text fontSize="sm" color="gray.600">
                  <Badge colorScheme="red" mr={2}>
                    Warning
                  </Badge>
                  Mandatory fields are automatically visible. You can only hide
                  optional fields.
                </Text>
              </Box>

              <Button
                colorScheme="blue"
                isLoading={loading}
                loadingText="Saving..."
                onClick={handleSave}
                px={8}
                size="md"
                w={{ base: "100%", md: "auto" }}
              >
                Save Changes
              </Button>
            </Flex>
          </CardBody>
        </Card>

        {/* Main Table Area */}
        <Card variant="outline" boxShadow="sm" bg="white" w="100%">
          <CardBody p={0}>
            <TableContainer>
              <Table variant="simple" size="md">
                <Thead bg="gray.100">
                  <Tr>
                    <Th fontSize={{ base: "xs", md: "sm" }}>Field Name</Th>
                    <Th fontSize={{ base: "xs", md: "sm" }} textAlign="center">
                      Mandatory
                      <br />
                      <Text fontSize="xs" fontWeight="normal">
                        (Required)
                      </Text>
                    </Th>
                    <Th fontSize={{ base: "xs", md: "sm" }} textAlign="center">
                      Visible
                      <br />
                      <Text fontSize="xs" fontWeight="normal">
                        (Show in Form)
                      </Text>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {localFields.map((field, index) => (
                    <Tr key={field.fieldName} _hover={{ bg: "gray.50" }}>
                      <Td
                        fontWeight="medium"
                        fontSize={{ base: "sm", md: "md" }}
                      >
                        {field.label}
                      </Td>

                      {/* Mandatory Switch */}
                      <Td textAlign="center">
                        <Switch
                          size="md"
                          colorScheme="red"
                          isChecked={field.isRequired}
                          onChange={() => handleToggle(index, "isRequired")}
                          // Prevent toggling critical system fields if necessary (e.g., Name)
                          isDisabled={
                            field.fieldName === "Name" ||
                            field.fieldName === "Phone1" ||
                            field.fieldName === "branchId" ||
                            field.fieldName === "cityId" ||
                            field.fieldName === "assignId"
                          }
                        />
                      </Td>

                      {/* Visible Switch */}
                      <Td textAlign="center">
                        <Switch
                          size="md"
                          colorScheme="teal"
                          isChecked={field.isVisible}
                          onChange={() => handleToggle(index, "isVisible")}
                          // LOGIC: Disable visibility switch if the field is Mandatory
                          isDisabled={
                            field.isRequired ||
                            field.fieldName === "Name" ||
                            field.fieldName === "Phone1" ||
                            field.fieldName === "branchId" ||
                            field.fieldName === "cityId" ||
                            field.fieldName === "assignId"
                          }
                          opacity={field.isRequired ? 0.6 : 1}
                          cursor={field.isRequired ? "not-allowed" : "pointer"}
                        />
                        {field.isRequired && (
                          <Text fontSize="xs" color="gray.400" mt={1}>
                            Locked (Mandatory)
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      </Container>
    </Box>
  );
};

export default LeadConfiguration;
